// SkipCustomerDetailPage — /skip/customers/:id.
//
// Read-only summary of a skip-branch customer plus the jobs and enquiries
// for that customer. Editing routes back to the shared
// /customers/:id/edit page (the underlying row lives in the unified
// `customers` table; we don't duplicate the form here).

import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { useSkipCustomer } from '../hooks/useSkipCustomers';
import { useSkipJobs } from '../hooks/useSkipJobs';
import { useSkipEnquiries } from '../hooks/useSkipEnquiries';
import { SkipJobStatusBadge } from '../jobs/components/SkipJobStatusBadge';
import { SkipEnquiryStatusBadge } from '../enquiries/components/SkipEnquiryStatusBadge';
import { CustomerSitesCard } from './components/CustomerSitesCard';
import styles from './SkipCustomerDetailPage.module.css';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function SkipCustomerDetailPage(): JSX.Element {
  const { id: encId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const customerQuery = useSkipCustomer(encId);
  const jobsQuery = useSkipJobs({ customer_uuid: customerQuery.data?.customer.uuid, limit: 50 });
  const enquiriesQuery = useSkipEnquiries({ limit: 50 });

  if (customerQuery.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="Loading customer…" />
      </div>
    );
  }

  if (customerQuery.isError || !customerQuery.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Customer not found" />
        <Card>
          <p>This customer could not be loaded.</p>
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/skip/customers')}>
            Back to Customers
          </Button>
        </Card>
      </div>
    );
  }

  const customer = customerQuery.data.customer;
  // Filter enquiries down to those whose customer matches this one. The
  // skip-enquiries list endpoint doesn't yet expose a customer_uuid filter
  // (jobs does, enquiries does not), so we filter client-side over the
  // first page of enquiries — adequate for v1 detail page volumes.
  const customerEnquiries = (enquiriesQuery.data?.enquiries ?? []).filter(
    (e) => e.customer_uuid === customer.uuid,
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title={customer.name}
        subtitle="Skip-branch customer details"
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="secondary"
              icon={ArrowLeft}
              onClick={() => navigate('/skip/customers')}
            >
              Back
            </Button>
            <Button
              variant="primary"
              icon={ExternalLink}
              onClick={() => navigate(`/customers/${encodeURIComponent(customer.uuid)}/edit`)}
            >
              Edit Customer
            </Button>
          </div>
        }
      />

      <Card>
        <h2 className={styles.sectionHeading}>Contact</h2>
        <dl className={styles.dl}>
          <dt>Name</dt>
          <dd>{customer.name}</dd>
          <dt>Email</dt>
          <dd>{customer.email ?? '—'}</dd>
          <dt>Phone</dt>
          <dd>{customer.phone}</dd>
          <dt>Postcode</dt>
          <dd>{customer.postcode ?? '—'}</dd>
          <dt>Address</dt>
          <dd>{customer.address ?? '—'}</dd>
        </dl>
      </Card>

      <CustomerSitesCard customerEncId={encId} />

      <Card>
        <h2 className={styles.sectionHeading}>Skip Jobs</h2>
        {jobsQuery.isLoading ? (
          <p className={styles.empty}>Loading jobs…</p>
        ) : (jobsQuery.data?.jobs.length ?? 0) === 0 ? (
          <p className={styles.empty}>No skip jobs for this customer yet.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Job #</th>
                <th>Type</th>
                <th>Delivery</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {jobsQuery.data?.jobs.map((j) => (
                <tr key={j.enc_id}>
                  <td className={styles.mono}>
                    <Link to={`/skip/jobs/${encodeURIComponent(j.enc_id)}`}>{j.job_number}</Link>
                  </td>
                  <td>{j.job_type.replaceAll('_', ' ')}</td>
                  <td>{fmtDate(j.delivery_date)}</td>
                  <td>
                    <SkipJobStatusBadge status={j.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <h2 className={styles.sectionHeading}>Skip Enquiries</h2>
        {enquiriesQuery.isLoading ? (
          <p className={styles.empty}>Loading enquiries…</p>
        ) : customerEnquiries.length === 0 ? (
          <p className={styles.empty}>No skip enquiries for this customer.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Enquiry #</th>
                <th>Type</th>
                <th>Created</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {customerEnquiries.map((e) => (
                <tr key={e.enc_id}>
                  <td className={styles.mono}>
                    <Link to={`/skip/enquiries/${encodeURIComponent(e.enc_id)}`}>
                      {e.enquiry_number}
                    </Link>
                  </td>
                  <td>{e.skip_stock_type_name ?? e.skip_type ?? '—'}</td>
                  <td>{fmtDate(e.created_at)}</td>
                  <td>
                    <SkipEnquiryStatusBadge status={e.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
